import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { TextField, TextFieldTheme, TextButton, TextButtonPriority } from "@wix/wix-ui-tpa/cssVars";
import classNames from "classnames";
import { useRef, useMemo } from "react";
import { useConfig } from "../../contexts/config-context.js";
import { useForcedState, ForcedState } from "../../contexts/forced-state-context.js";
import { useTranslation } from "../../contexts/translation-context.js";
import { FieldLabelWUT } from "../field-label/field-label-wut.js";
import { deserialize, serialize } from "./helpers.js";
import { TimeInput } from "./time-input/time-input.js";
import { classes } from "./time-picker-wut.st.css";
function TimePickerWUT({
  value,
  onChange,
  className,
  disabled,
  hidePlaceholder,
  use24Hours,
  readOnly,
  showTimeLabel,
  inputId,
  ...rest
}) {
  const { isMobile } = useConfig();
  const { t } = useTranslation();
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const nativeRef = useRef(null);
  const ampmRef = useRef(null);
  const minutesRef = useRef(null);
  const placeholderValue = useMemo(
    () => hidePlaceholder ? "--:--" : t("field.date.placeholder.time"),
    [hidePlaceholder, t]
  );
  const { hours, minutes, period } = deserialize(value, use24Hours);
  const periodText = t(`field.time.perdiod.${period}`);
  const focusOnAMPM = () => {
    ampmRef.current?.focus();
  };
  const handleNativeChange = (event) => {
    const [newHours, newMinutes] = (event.target?.value ?? "").split(":");
    onChange(serialize(newHours, newMinutes));
  };
  const showPicker = () => {
    if (isMobile) {
      nativeRef.current?.showPicker();
    }
  };
  const handlePeriodToggle = () => {
    if (readOnly) {
      return;
    }
    onChange(
      serialize(hours, minutes, period === "AM" ? "PM" : "AM", use24Hours)
    );
  };
  const handleAMPMKeyDown = (event) => {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        handlePeriodToggle();
        break;
      }
      case "ArrowLeft": {
        minutesRef.current?.focus();
        break;
      }
    }
  };
  return /* @__PURE__ */ jsxs("div", { onClick: showPicker, className: classNames(classes.root, className), children: [
    /* @__PURE__ */ jsx(
      FieldLabelWUT,
      {
        labelId: `form-field-sub-label-time-${inputId}`,
        label: t("field.date.label.time"),
        hideLabel: !showTimeLabel,
        required: false
      }
    ),
    /* @__PURE__ */ jsx(
      TextField,
      {
        ...rest,
        theme: TextFieldTheme.Box,
        newErrorMessage: true,
        disabled,
        inputClassName: classes.hidden,
        className: classNames(className, classes.textField, classes.theme, {
          [classes.error]: rest.error,
          [classes.hover]: forcedHover
        }),
        prefix: /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              tabIndex: -1,
              type: "time",
              ref: nativeRef,
              value,
              "data-time-field": true,
              hidden: !isMobile,
              onChange: handleNativeChange,
              className: classes.native
            }
          ),
          /* @__PURE__ */ jsx(
            TimeInput,
            {
              inputId,
              placeholder: placeholderValue,
              onChange: (newHours, newMinutes, newPeriod) => {
                onChange(
                  serialize(
                    newHours,
                    newMinutes,
                    newPeriod ?? period,
                    use24Hours
                  )
                );
              },
              disabled,
              value,
              use24Hours,
              onNext: focusOnAMPM,
              minutesRef,
              readOnly
            }
          ),
          !use24Hours && /* @__PURE__ */ jsx(
            TextButton,
            {
              "data-time-field": true,
              "aria-label": `${t("field.time.label.period")} ${periodText}`,
              "data-hook": "ampm",
              onClick: handlePeriodToggle,
              onKeyDown: handleAMPMKeyDown,
              className: classNames([
                classes.period,
                (hours || minutes) && classes.period__active
              ]),
              priority: TextButtonPriority.secondary,
              "aria-live": "polite",
              ref: ampmRef,
              children: periodText
            }
          )
        ] })
      }
    )
  ] });
}
export {
  TimePickerWUT
};
//# sourceMappingURL=time-picker-wut.js.map
